import ApiPaths from "@/apis/ApiPaths";
import ApiMetrics from "@/apis/ApiMetrics";
import { defineStore } from "pinia";
import * as Sentry from "@sentry/vue";
import { computed, ref } from "vue";
import { exceptionWrapper, useTableData } from "./piniaUtils";

export const usePromptsStore = defineStore("prompts", () => {
  const prompts = ref([]);
  const loading = ref(true);
  const error = ref(false);
  const isPollingPrompts = ref(false);
  const promptsWarm = ref({ state: "idle" });
  const lastWarmRequest = ref(null);
  const loadedAppId = ref(null);
  const warmPending = computed(() => promptsWarm.value.state !== "idle");
  const warmFailed = computed(() => promptsWarm.value.state === "failed");
  const { table, resetPage, setPagination, reloadAllLoaded, loadingMore } = useTableData();

  const getPrompts = (appId, params) =>
    exceptionWrapper(
      async () => {
        prompts.value = await ApiPaths.getPrompts(appId, params);
        table.page = 0;
        table.totalCount = 0;
        table.totalPages = 1;
      },
      { loading, error, toast: true },
    );

  const getPromptsPaged = (appId, params, next = false, { signal } = {}) =>
    exceptionWrapper(
      async () => {
        // blank only on app switches — keep prior rows within an app until data lands
        if (!next && loadedAppId.value !== appId) {
          prompts.value = [];
          promptsWarm.value = { state: "idle" };
          lastWarmRequest.value = null;
        }
        const requestParams = {
          ...params,
          serialize: "list",
          page: table.page + 1,
          per_page: table.perPage,
        };
        lastWarmRequest.value = { appId, params, next };
        let pinnedKey = null;
        let response = await ApiPaths.getPrompts(appId, requestParams, {
          signal,
          statusKey: pinnedKey,
        });
        // RE-1459: poll the warm job, then refetch with the pinned status key;
        // FAILED ends in the banner state — never a thrown error (PULSE-4W9)
        for (
          let attempt = 0;
          response?.async && response?.status_key && attempt < 3;
          attempt += 1
        ) {
          pinnedKey = response.status_key;
          if (response.status === "FAILED") {
            // backend reports the cooldown-held FAILED directly — skip the poll
            Sentry.addBreadcrumb({
              category: "metrics",
              level: "warning",
              message: "Metrics warm job failed",
              data: { statusKey: pinnedKey },
            });
            promptsWarm.value = { state: "failed" };
            return;
          }
          promptsWarm.value = { state: "warming" };
          isPollingPrompts.value = true;
          try {
            await ApiMetrics.pollMetricsStatus(appId, { statusKey: pinnedKey });
          } catch (err) {
            if (err?.name === "AbortError") throw err;
            Sentry.addBreadcrumb({
              category: "metrics",
              level: "warning",
              message: "Metrics warm job failed",
              data: { statusKey: err.statusKey, detail: err.detail },
            });
            promptsWarm.value = { state: "failed", detail: err.detail };
            return;
          } finally {
            isPollingPrompts.value = false;
          }
          response = await ApiPaths.getPrompts(appId, requestParams, {
            signal,
            statusKey: pinnedKey,
          });
        }
        if (response?.async && response?.status_key) {
          // poll budget exhausted — keep the banner up instead of throwing
          promptsWarm.value = { state: "warming" };
          return;
        }

        promptsWarm.value = { state: "idle" };
        lastWarmRequest.value = null;
        loadedAppId.value = appId;
        const { paths, pagination } = response;

        if (next) {
          const promptsFiltered = paths.filter(el => !prompts.value.find(({ id }) => id === el.id));
          prompts.value = [...prompts.value, ...promptsFiltered];
        } else {
          prompts.value = paths;
        }

        setPagination(pagination);
      },
      { loading: next ? loadingMore : loading, error, toast: true },
    );

  const retryWarm = () => {
    const last = lastWarmRequest.value;
    if (!last) return Promise.resolve();
    return getPromptsPaged(last.appId, last.params, last.next);
  };

  const updateLocalPrompt = prompt => {
    if (!prompts.value.length) return;
    const promptIndex = prompts.value.findIndex(({ id }) => id === prompt.id);
    const promptsCopy = [...prompts.value];
    if (promptIndex === -1) return;
    promptsCopy[promptIndex] = { ...prompts.value[promptIndex], ...prompt };
    prompts.value = promptsCopy;
  };

  const bulkUpdate = (appId, promptsIn = []) =>
    exceptionWrapper(
      async () => {
        await ApiPaths.bulkUpdate(appId, promptsIn);
        promptsIn.forEach(updateLocalPrompt);
      },
      { toast: true, throw: true },
    );

  const bulkDelete = (appId, promptsIn = []) =>
    exceptionWrapper(
      () =>
        ApiPaths.bulkDelete(
          appId,
          promptsIn.map(({ id }) => ({ id })),
        ),
      { toast: true, throw: true },
    );

  const bulkTagUpdate = (appId, pathIds, tagIdsToAdd = [], tagIdsToRemove = []) =>
    exceptionWrapper(
      async () => {
        await ApiPaths.bulkTagUpdate(appId, pathIds, tagIdsToAdd, tagIdsToRemove);
      },
      { toast: true, throw: true },
    );

  return {
    prompts,
    loading,
    loadingMore,
    error,
    isPollingPrompts,
    warmPending,
    warmFailed,
    retryWarm,
    getPrompts,
    getPromptsPaged,
    table,
    resetPage,
    reloadAllLoaded,
    updateLocalPrompt,
    bulkUpdate,
    bulkDelete,
    bulkTagUpdate,
  };
});
